import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { XCircle, Lock, Info, Ban, Clock3, Loader2, Plus } from "lucide-react";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";
import DepartmentSelector from "../../components/selectors/DepartmentSelector";

export default function EditUserPage() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const hasEditPermission = checkPermission("user_update");
  const canManageTemporaryAssignments = checkPermission("temporary_department_assignment_manage");

  useEffect(() => {
    if (!hasEditPermission) {
      navigate("/employees");
    }
  }, [hasEditPermission, navigate]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [file, setFile] = useState<File | undefined>(undefined);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [extensionEndsAt, setExtensionEndsAt] = useState("");
  const [assignmentForm, setAssignmentForm] = useState({
    targetDepartmentId: "",
    startsAt: new Date().toISOString().slice(0, 16),
    endsAt: "",
    reason: "",
  });
  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700";
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    departmentId: null as string | null,
    birthDate: "" as string,
    metadata: {
      schoolNumber: "",
      tcNumber: "",
      parentPhone: "",
      parentEmail: "",
      orderCode: "",
      orderDate: "",
    },
    paymentStatus: 2,
    licenseDays: null as number | null,
    isUnlimitedLicense: true,
    licenseExpiryDate: null as string | null,
    lastPaymentDate: null as string | null,
    isBlock: false,
    isVerified: false,
    isSuperAdmin: false,
  });

  useEffect(() => {
    const load = async () => {
      if (!userId) return;
      setError("");
      setSuccess("");
      try {
        setInitialLoading(true);
        const user = await ApiService.getUser(userId);
        const loadedAssignments = canManageTemporaryAssignments
          ? await ApiService.getTemporaryAssignments(userId).catch(() => [])
          : user.temporaryDepartmentAssignments || [];
        setAssignments(loadedAssignments);
        setExistingPhotoUrl(user.media?.url || null);
        setForm({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
          password: "",
          departmentId: user.departmentId || null,
          birthDate: user.birthDate || "",
          metadata: {
            schoolNumber: "",
            tcNumber: "",
            parentPhone: "",
            parentEmail: "",
            orderCode: "",
            orderDate: "",
            ...(user.metadata || {}),
          },
          paymentStatus: user.paymentStatus !== undefined ? user.paymentStatus : 2,
          licenseDays: user.licenseDays || null,
          isUnlimitedLicense: user.isUnlimitedLicense !== undefined ? user.isUnlimitedLicense : true,
          licenseExpiryDate: user.licenseExpiryDate || null,
          lastPaymentDate: user.lastPaymentDate || null,
          isBlock: !!user.isBlock,
          isVerified: !!user.isVerified,
          isSuperAdmin: !!user.isSuperAdmin,
        });
      } catch (e: any) {
        setError(e?.message || "Kullanıcı bilgileri yüklenemedi");
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [canManageTemporaryAssignments, userId]);

  const activeAssignment = assignments.find((item) => ["ACTIVE", "SCHEDULED"].includes(item.status));

  const createAssignment = async () => {
    if (!userId || !assignmentForm.targetDepartmentId || !assignmentForm.startsAt || !assignmentForm.endsAt) {
      setError("Süreli atama için hedef departman ve tarihler zorunludur.");
      return;
    }
    setAssignmentLoading(true);
    setError("");
    try {
      await ApiService.createTemporaryAssignment({
        userId,
        targetDepartmentId: assignmentForm.targetDepartmentId,
        startsAt: new Date(assignmentForm.startsAt).toISOString(),
        endsAt: new Date(assignmentForm.endsAt).toISOString(),
        reason: assignmentForm.reason || undefined,
      });
      setAssignments(await ApiService.getTemporaryAssignments(userId));
      setSuccess("Süreli departman ataması oluşturuldu.");
    } catch (e: any) {
      setError(e?.message || "Süreli atama oluşturulamadı.");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const endAssignment = async (assignmentId: string) => {
    setAssignmentLoading(true);
    setError("");
    try {
      await ApiService.endTemporaryAssignment(assignmentId);
      if (userId) setAssignments(await ApiService.getTemporaryAssignments(userId));
      setSuccess("Süreli departman ataması sonlandırıldı.");
    } catch (e: any) {
      setError(e?.message || "Süreli atama sonlandırılamadı.");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const extendAssignment = async (assignmentId: string) => {
    if (!extensionEndsAt) {
      setError("Yeni bitiş tarihi zorunludur.");
      return;
    }
    setAssignmentLoading(true);
    setError("");
    try {
      await ApiService.extendTemporaryAssignment(assignmentId, new Date(extensionEndsAt).toISOString());
      if (userId) setAssignments(await ApiService.getTemporaryAssignments(userId));
      setExtensionEndsAt("");
      setSuccess("Süreli departman ataması uzatıldı.");
    } catch (e: any) {
      setError(e?.message || "Süreli atama uzatılamadı.");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!userId) return;
    if (!form.firstName || !form.lastName || !form.email || !form.departmentId) {
      setError("Lütfen zorunlu alanları doldurun.");
      return;
    }

    if (form.password && form.password.length < 8) {
      setError("Şifre en az 8 karakter olmalıdır.");
      return;
    }

    if (form.metadata?.tcNumber && form.metadata.tcNumber.length !== 11) {
      setError("TC Kimlik Numarası tam 11 rakam olmalıdır.");
      return;
    }

    if (form.phone && form.phone.length !== 10) {
      setError("Kişinin Telefon Numarası tam 10 rakam olmalıdır.");
      return;
    }

    if (form.metadata?.parentPhone && form.metadata.parentPhone.length !== 10) {
      setError("Veli Telefon Numarası tam 10 rakam olmalıdır.");
      return;
    }
    try {
      setLoading(true);
      await ApiService.updateUser(
        userId,
        {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          birthDate: form.birthDate || null,
          ...(form.password ? { password: form.password } : {}),
          departmentId: form.departmentId || null,
          metadata: form.metadata || {},
          paymentStatus: form.isBlock ? form.paymentStatus : form.paymentStatus,
          licenseDays: form.isBlock ? null : form.licenseDays,
          isUnlimitedLicense: form.isBlock ? form.isUnlimitedLicense : form.isUnlimitedLicense,
          lastPaymentDate: form.isBlock ? null : form.lastPaymentDate,
          isBlock: form.isBlock,
        },
        file
      );
      setSuccess("Kullanıcı güncellendi!");
      navigate("/employees?tab=users&updated=" + Date.now());
    } catch (e: any) {
      setError(e?.message || "Kullanıcı güncellenemedi");
    } finally {
      setLoading(false);
    }
  };

  if (!hasEditPermission) {
    return null;
  }

  return (
    <div className="h-[88vh] bg-gray-100 overflow-hidden flex flex-col">
      <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Üyeler</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <button
                onClick={() => navigate("/employees?tab=users")}
                className={`pb-3 transition-all duration-200 text-gray-900 font-semibold border-b-2 border-gray-900`}>
                Kullanıcılar
              </button>
              <button
                onClick={() => navigate("/employees?tab=departments")}
                className={`pb-3 transition-all duration-200 text-gray-500 hover:text-gray-900`}>
                Departmanlar
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Kullanıcıyı Düzenle</h1>
                <p className="text-sm text-gray-600 mt-1">Bilgileri güncelleyin ve departmanını yönetin.</p>
                <div className="mt-3 h-1 w-20 rounded-full bg-blue-200" />
              </div>
              <div className="text-right">
                {form.firstName && form.lastName && (
                  <div>
                    <p className="text-gray-900 font-semibold text-base sm:text-lg">
                      {form.firstName} {form.lastName}
                    </p>
                    <p className="text-gray-600 text-sm">{form.email}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Kullanıcı Bilgileri</h2>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${form && (form as any).isSuperAdmin ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600"
                    }`}>
                  {(form as any).isSuperAdmin ? "Süper Admin" : "Standart"}
                </span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${form && (form as any).isVerified ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                    }`}>
                  {(form as any).isVerified ? "E-posta Doğrulandı" : "E-posta Bekliyor"}
                </span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${form && (form as any).isBlock ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                    }`}>
                  {(form as any).isBlock ? "Engelli" : "Aktif"}
                </span>
              </div>
            </div>

            {initialLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : (
              <form id="edit-user-form" onSubmit={handleSubmit} className="space-y-6">
                {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">{error}</div>}
                {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm p-3 rounded-lg">{success}</div>}

                <div className={sectionClassName}>
                  <div className="mb-3">
                    <span className={sectionBadgeClassName}>
                      Temel Bilgiler
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Profil Fotoğrafı</label>
                    <label className="group block cursor-pointer">
                      <div className="rounded-2xl border border-blue-100 bg-gradient-to-b from-blue-50 to-white p-4">
                        <div className="mx-auto h-36 w-36 rounded-full border-2 border-dashed border-blue-200 bg-white shadow-sm overflow-hidden flex items-center justify-center transition group-hover:border-blue-300 group-hover:shadow">
                          {file ? (
                            <img src={URL.createObjectURL(file)} alt="avatar" className="h-full w-full object-cover" />
                          ) : existingPhotoUrl ? (
                            <img src={existingPhotoUrl} alt="avatar" className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-xs text-slate-400">Fotoğraf Önizleme</span>
                          )}
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <span className="text-xs text-slate-500">JPG veya PNG</span>
                          <span className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition group-hover:border-blue-300 group-hover:text-blue-700">
                            {existingPhotoUrl ? "Fotoğrafı Değiştir" : "Dosya Seç"}
                          </span>
                        </div>
                      </div>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} />
                    </label>
                  </div>
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ad *</label>
                      <input
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        className={fieldClassName}
                        placeholder="Ahmet"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Soyad *</label>
                      <input
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        className={fieldClassName}
                        placeholder="Yılmaz"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">E-posta *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        className={fieldClassName}
                        placeholder="ahmet.yilmaz@gmail.com"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Telefon Numarası</label>
                      <input
                        name="phone"
                        value={form.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 10) {
                            setForm((prev) => ({
                              ...prev,
                              phone: value,
                            }));
                          }
                        }}
                        className={fieldClassName}
                        placeholder="5321234567"
                        maxLength={10}
                        pattern="[0-9]{10}"
                        title="Telefon Numarası tam 10 rakam olmalıdır (örn: 5321234567)"
                      />
                      {form.phone && form.phone.length !== 10 && (
                        <p className="mt-1.5 text-xs text-red-600">Telefon Numarası tam 10 rakam olmalıdır</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Yeni Şifre</label>
                      <input
                        type="password"
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        className={fieldClassName}
                        placeholder="En Az 8 Karakter (Boş Bırakabilirsiniz)"
                        minLength={8}
                        title="Şifre en az 8 karakter olmalıdır"
                      />
                      {form.password && form.password.length < 8 && (
                        <p className="mt-1.5 text-xs text-red-600">Şifre en az 8 karakter olmalıdır</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Doğum Tarihi</label>
                      <input
                        type="date"
                        name="birthDate"
                        value={form.birthDate}
                        onChange={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            birthDate: e.target.value,
                          }));
                        }}
                        className={fieldClassName}
                        max={new Date().toISOString().split("T")[0]}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Departman *</label>
                      <DepartmentSelector
                        value={form.departmentId}
                        onChange={(departmentId) =>
                          setForm((p) => ({
                            ...p,
                            departmentId: departmentId || null,
                          }))
                        }
                        placeholder="Departman Seçin"
                        className="w-full rounded-xl"
                        required={true}
                        allowClear={false}
                        showColors={true}
                        disabled={Boolean(activeAssignment)}
                      />
                      {activeAssignment && <p className="mt-2 text-xs text-amber-700">Aktif süreli atama sonlandırılmadan departman değiştirilemez.</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Okul Numarası</label>
                      <input
                        name="metadata.schoolNumber"
                        value={form.metadata?.schoolNumber}
                        onChange={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            metadata: {
                              ...prev.metadata,
                              schoolNumber: e.target.value,
                            },
                          }));
                        }}
                        className={fieldClassName}
                        placeholder="123"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">TC Kimlik Numarası</label>
                      <input
                        name="metadata.tcNumber"
                        value={form.metadata?.tcNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 11) {
                            setForm((prev) => ({
                              ...prev,
                              metadata: {
                                ...prev.metadata,
                                tcNumber: value,
                              },
                            }));
                          }
                        }}
                        className={fieldClassName}
                        placeholder="12345678901"
                        maxLength={11}
                        pattern="[0-9]{11}"
                        title="TC Kimlik Numarası tam 11 rakam olmalıdır"
                      />
                      {form.metadata?.tcNumber && form.metadata.tcNumber.length !== 11 && (
                        <p className="mt-1.5 text-xs text-red-600">TC Kimlik Numarası tam 11 rakam olmalıdır</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Veli Telefon Numarası</label>
                      <input
                        name="metadata.parentPhone"
                        value={form.metadata?.parentPhone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 10) {
                            setForm((prev) => ({
                              ...prev,
                              metadata: {
                                ...prev.metadata,
                                parentPhone: value,
                              },
                            }));
                          }
                        }}
                        className={fieldClassName}
                        placeholder="5321234567"
                        maxLength={10}
                        pattern="[0-9]{10}"
                        title="Veli Telefon Numarası tam 10 rakam olmalıdır (örn: 5321234567)"
                      />
                      {form.metadata?.parentPhone && form.metadata.parentPhone.length !== 10 && (
                        <p className="mt-1.5 text-xs text-red-600">Veli Telefon Numarası tam 10 rakam olmalıdır</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Veli E-posta</label>
                      <input
                        type="email"
                        name="metadata.parentEmail"
                        value={form.metadata?.parentEmail}
                        onChange={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            metadata: {
                              ...prev.metadata,
                              parentEmail: e.target.value,
                            },
                          }));
                        }}
                        className={fieldClassName}
                        placeholder="mehmet.yilmaz@gmail.com"
                      />
                    </div>
                  </div>
                </div>
                </div>

                <div className={sectionClassName}>
                  <div className="mb-3">
                    <span className={sectionBadgeClassName}>Lisans ve Odeme</span>
                  </div>
                  <h3 className="text-md font-semibold text-gray-900 mb-5">Lisans ve Ödeme Bilgileri</h3>
                  {!form.isBlock && !form.isSuperAdmin && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ödeme Durumu</label>
                        <select
                          value={form.paymentStatus}
                          disabled={form.isBlock}
                          onChange={(e) => {
                            const newStatus = parseInt(e.target.value);

                            if (newStatus === 0) {
                              const defaultDays = 7;
                              const expiryDate = new Date();
                              expiryDate.setDate(expiryDate.getDate() + defaultDays);
                              const lastPayment = new Date(expiryDate);

                              setForm((prev) => ({
                                ...prev,
                                paymentStatus: newStatus,
                                isUnlimitedLicense: false,
                                licenseDays: defaultDays,
                                licenseExpiryDate: expiryDate.toISOString(),
                                lastPaymentDate: lastPayment.toISOString(),
                              }));
                            } else {
                              setForm((prev) => ({
                                ...prev,
                                paymentStatus: newStatus,
                                isUnlimitedLicense: true,
                                licenseDays: null,
                                licenseExpiryDate: null,
                                lastPaymentDate: null,
                              }));
                            }
                          }}
                          className={`w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition ${form.isBlock ? "cursor-not-allowed bg-gray-100" : "hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50"
                            }`}>
                          <option value={0}>Ödeme Yapılmadı</option>
                          <option value={1}>Ödeme Bekleniyor</option>
                          <option value={2}>Ödeme Yapıldı</option>
                          <option value={3}>Dilekçeli</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sipariş Kodu</label>
                        <input
                          name="metadata.orderCode"
                          value={form.metadata?.orderCode || ""}
                          onChange={(e) => {
                            setForm((prev) => ({
                              ...prev,
                              metadata: {
                                ...prev.metadata,
                                orderCode: e.target.value,
                              },
                            }));
                          }}
                          className={fieldClassName}
                          placeholder="ORD-2025-0001"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sipariş Tarihi</label>
                        <input
                          type="date"
                          name="metadata.orderDate"
                          value={form.metadata?.orderDate || ""}
                          max={new Date().toISOString().split("T")[0]}
                          onChange={(e) => {
                            setForm((prev) => ({
                              ...prev,
                              metadata: {
                                ...prev.metadata,
                                orderDate: e.target.value,
                              },
                            }));
                          }}
                          className={fieldClassName}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Lisans Tipi</label>
                        <select
                          value={form.isUnlimitedLicense ? "unlimited" : "limited"}
                          disabled={true}
                          className="w-full rounded-xl border border-slate-200 bg-gray-100 px-3.5 py-2.5 text-sm text-slate-600 cursor-not-allowed">
                          <option value="unlimited">Sınırsız Lisans</option>
                          <option value="limited">Süreli Lisans</option>
                        </select>
                        <p className="mt-1.5 text-xs text-gray-500">
                          {form.paymentStatus === 0
                            ? "Ödeme Yapılmadı - Otomatik Süreli Lisans"
                            : "Ödeme Durumu Aktif - Otomatik Sınırsız Lisans"}
                        </p>
                      </div>

                      {form.paymentStatus === 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Lisans Gün Sayısı *</label>
                          <input
                            type="number"
                            min="1"
                            max="30"
                            value={form.licenseDays || ""}
                            disabled={form.isBlock}
                            onChange={(e) => {
                              const days = e.target.value ? parseInt(e.target.value) : null;
                              if (days && days <= 30) {
                                const expiryDate = new Date();
                                expiryDate.setDate(expiryDate.getDate() + days);
                                const lastPayment = new Date(expiryDate);

                                setForm((prev) => ({
                                  ...prev,
                                  licenseDays: days,
                                  licenseExpiryDate: expiryDate.toISOString(),
                                  lastPaymentDate: lastPayment.toISOString(),
                                }));
                              } else if (!days) {
                                setForm((prev) => ({
                                  ...prev,
                                  licenseDays: null,
                                  licenseExpiryDate: null,
                                  lastPaymentDate: null,
                                }));
                              }
                            }}
                            className={`w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition ${form.isBlock ? "cursor-not-allowed bg-gray-100" : "hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50"
                              }`}
                            placeholder="30"
                            required={!form.isBlock}
                          />
                          <p className="mt-1.5 text-xs text-gray-500">Maksimum 30 gün geçerli olabilir.</p>
                        </div>
                      )}

                      {form.paymentStatus === 0 && form.lastPaymentDate && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Son Ödeme Tarihi</label>
                          <input
                            type="text"
                            value={new Date(form.lastPaymentDate).toLocaleDateString("tr-TR", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3.5 py-2.5 text-sm text-slate-700"
                            disabled
                          />
                          <p className="mt-1.5 text-xs text-gray-500">Hesap bu tarihe kadar aktif kalacak.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {!form.isBlock && !form.isSuperAdmin && form.paymentStatus === 0 && form.lastPaymentDate && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <XCircle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-red-800 mb-1">Ödeme Yapılmadı - Hesap Engelleme Uyarısı</h4>
                          <p className="text-xs text-red-700">
                            Bu kullanıcının hesabı{" "}
                            <strong>
                              {Math.ceil((new Date(form.lastPaymentDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} gün
                              sonra
                            </strong>{" "}
                            (
                            {new Date(form.lastPaymentDate).toLocaleDateString("tr-TR", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            })}
                            ) otomatik olarak pasife alınacak ve sisteme girişi engellenecektir.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {(form.isBlock || form.isSuperAdmin) && (
                    <div className="mt-4 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <Lock className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-1">
                            {form.isSuperAdmin ? "Süper Admin - Lisans Ayarları Kilitli" : "Lisans Ayarları Kilitlendi"}
                          </h4>
                          <p className="text-xs text-gray-700">
                            {form.isSuperAdmin
                              ? "Süper Admin hesaplarında lisans ve ödeme ayarları değiştirilemez."
                              : "Hesap engellendiği için lisans ve ödeme ayarları değiştirilemez. Bu ayarları düzenlemek için önce hesap engelini kaldırın."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className={sectionClassName}>
                  <div className="mb-3">
                    <span className={sectionBadgeClassName}>Hesap Guvenligi</span>
                  </div>
                  <h3 className="text-md font-semibold text-gray-900 mb-5">Hesap Durumu</h3>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.isBlock}
                        disabled={form.isSuperAdmin}
                        onChange={(e) => {
                          const nextIsBlock = e.target.checked;
                          setForm((prev) => {
                            if (nextIsBlock) {
                              return {
                                ...prev,
                                isBlock: true,
                                licenseDays: null,
                                licenseExpiryDate: null,
                                lastPaymentDate: null,
                              };
                            } else {
                              if (prev.paymentStatus === 0) {
                                const defaultDays = 7;
                                const expiryDate = new Date();
                                expiryDate.setDate(expiryDate.getDate() + defaultDays);
                                const lastPayment = new Date(expiryDate);

                                return {
                                  ...prev,
                                  isBlock: false,
                                  isUnlimitedLicense: false,
                                  licenseDays: defaultDays,
                                  licenseExpiryDate: expiryDate.toISOString(),
                                  lastPaymentDate: lastPayment.toISOString(),
                                };
                              } else {
                                return {
                                  ...prev,
                                  isBlock: false,
                                  isUnlimitedLicense: true,
                                  licenseDays: null,
                                  licenseExpiryDate: null,
                                  lastPaymentDate: null,
                                };
                              }
                            }
                          });
                        }}
                        className={`w-4 h-4 ${form.isSuperAdmin ? "text-gray-400 cursor-not-allowed" : "text-red-600"
                          } bg-gray-100 border-gray-300 rounded ${form.isSuperAdmin ? "" : "focus:ring-red-500"}`}
                      />
                      <span className="text-sm font-medium text-gray-700">Hesabı Engelle</span>
                    </label>
                  </div>
                  {form.isSuperAdmin && (
                    <div className="mt-3 p-4 bg-white border border-slate-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <Info className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-slate-800 mb-1">Süper Admin - Hesap Durumu Kilitli</h4>
                          <p className="text-xs text-slate-600">Süper Admin hesaplarında hesap durumu değiştirilemez.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {form.isBlock && (
                    <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <Ban className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-red-800 mb-1">Hesap Engellenecek</h4>
                          <p className="text-xs text-red-700">
                            Bu kullanıcı sisteme giriş yapamayacak ve tüm yetkilerini kaybedecektir. Bu işlemi onaylamadan önce emin olun.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {canManageTemporaryAssignments && (
                  <div className={sectionClassName}>
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <span className={sectionBadgeClassName}>Departman Yönetimi</span>
                        <h3 className="mt-2 flex items-center gap-2 text-md font-semibold text-gray-900"><Clock3 className="h-5 w-5 text-blue-600" />Süreli Departman Ataması</h3>
                        <p className="mt-1 text-sm text-gray-600">Kullanıcıyı belirli bir süre için başka bir departmana taşıyın.</p>
                      </div>
                    </div>

                    {activeAssignment ? (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="font-semibold text-amber-900">{activeAssignment.previousDepartment?.name || "Önceki departman"} → {activeAssignment.targetDepartment?.name || "Hedef departman"}</div>
                            <div className="mt-1 text-xs text-amber-700">{new Date(activeAssignment.startsAt).toLocaleString("tr-TR")} - {new Date(activeAssignment.endsAt).toLocaleString("tr-TR")}</div>
                            <div className="mt-1 text-xs text-amber-700">{activeAssignment.sourceType === "PAYMENT" ? "Ödeme kaynaklı" : activeAssignment.reason || "Manuel atama"}</div>
                          </div>
                          <button type="button" disabled={assignmentLoading} onClick={() => void endAssignment(activeAssignment.id)} className="rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">Erken Sonlandır</button>
                        </div>
                        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-amber-200 pt-4">
                          <label className="min-w-[220px] flex-1 text-xs font-medium text-amber-900">Yeni bitiş tarihi
                            <input type="datetime-local" min={new Date(activeAssignment.endsAt).toISOString().slice(0, 16)} value={extensionEndsAt} onChange={(event) => setExtensionEndsAt(event.target.value)} className="mt-1.5 w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm text-slate-800" />
                          </label>
                          <button type="button" disabled={assignmentLoading || !extensionEndsAt} onClick={() => void extendAssignment(activeAssignment.id)} className="rounded-lg bg-amber-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">Süreyi Uzat</button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="md:col-span-2">
                          <label className="mb-2 block text-sm font-medium text-gray-700">Hedef Departman</label>
                          <DepartmentSelector value={assignmentForm.targetDepartmentId} onChange={(targetDepartmentId) => setAssignmentForm((prev) => ({ ...prev, targetDepartmentId }))} excludeIds={form.departmentId ? [form.departmentId] : []} placeholder="Hedef departmanı seçin" />
                        </div>
                        <label className="text-sm font-medium text-gray-700">Başlangıç
                          <input type="datetime-local" value={assignmentForm.startsAt} onChange={(event) => setAssignmentForm((prev) => ({ ...prev, startsAt: event.target.value }))} className={`${fieldClassName} mt-2`} />
                        </label>
                        <label className="text-sm font-medium text-gray-700">Bitiş
                          <input type="datetime-local" value={assignmentForm.endsAt} onChange={(event) => setAssignmentForm((prev) => ({ ...prev, endsAt: event.target.value }))} className={`${fieldClassName} mt-2`} />
                        </label>
                        <label className="text-sm font-medium text-gray-700 md:col-span-2">Gerekçe
                          <input value={assignmentForm.reason} onChange={(event) => setAssignmentForm((prev) => ({ ...prev, reason: event.target.value }))} className={`${fieldClassName} mt-2`} placeholder="Atama gerekçesi" />
                        </label>
                        <div className="md:col-span-2">
                          <button type="button" onClick={() => void createAssignment()} disabled={assignmentLoading} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
                            {assignmentLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Atama Oluştur
                          </button>
                        </div>
                      </div>
                    )}

                    {assignments.length > 0 && (
                      <div className="mt-5 overflow-x-auto">
                        <table className="w-full min-w-[680px] text-left text-sm">
                          <thead className="border-b border-slate-200 text-xs uppercase text-slate-500"><tr><th className="py-2">Kaynak</th><th>Hedef</th><th>Başlangıç</th><th>Bitiş</th><th>Durum</th></tr></thead>
                          <tbody>{assignments.map((item) => <tr key={item.id} className="border-b border-slate-100"><td className="py-2">{item.previousDepartment?.name || item.previousDepartmentId}</td><td>{item.targetDepartment?.name || item.targetDepartmentId}</td><td>{new Date(item.startsAt).toLocaleString("tr-TR")}</td><td>{new Date(item.endsAt).toLocaleString("tr-TR")}</td><td>{item.status}</td></tr>)}</tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 text-sm">
                      {loading ? "Güncelleniyor..." : "Kullanıcıyı Güncelle"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
